/**
 * Cost estimate selection (S3.1) — pure, zero I/O.
 *
 * DD!96 Cost Estimate Type 成本估算類型 (J=Could): which stored figure feeds
 * the estimated-cost / estimated-margin preview. This module ONLY selects a
 * stored figure — it derives nothing: `buildCostBreakdown` (resolvePrice.ts)
 * stays the one canonical waterfall (Purchase Cost + Landed Cost Allocation =
 * Landed Cost ×(1+內部加價率) = Pricing Base ×(1+銷售加價率) = List Price,
 * pinned by the pricing-formula recheck) and is UNTOUCHED by S3.1.
 *
 * Shipped members and their citations live on `CostEstimateType`
 * (src/types/api/pricing.ts): item_defined_cost (DD!97, J=Must) ·
 * average_cost (DD!119 + board screenshot) · last_purchase_price (DD!122 +
 * board screenshot) · purchase_price (DD!44). 'derived_from_member_items' is
 * DROPPED (kit/assembly — out of scope); preferred_vendor_rate /
 * purchase_order_rate / custom are typed TODOs pending citation.
 *
 * Landed-cost opt-in + allocation basis ship as DATA on `CostRecord`
 * (`trackLandedCost`, `landedCostAllocationBasis` — DD!39; plan §S3.1); no
 * allocation math ships.
 */

import type {
  CostEstimateInputs,
  CostEstimateSelection,
  CostEstimateType,
} from '@/types/api/pricing';
import { roundHalfUp2 } from './quantityPricing';

/** The shipped (cited) members, in xlsx row order — UI select options source. */
export const COST_ESTIMATE_TYPES: readonly CostEstimateType[] = [
  'purchase_price', // DD!44
  'item_defined_cost', // DD!97
  'average_cost', // DD!119
  'last_purchase_price', // DD!122
] as const;

const INPUT_BY_TYPE: Record<CostEstimateType, keyof CostEstimateInputs> = {
  item_defined_cost: 'itemDefinedCost',
  average_cost: 'averageCost',
  last_purchase_price: 'lastPurchasePrice',
  purchase_price: 'purchasePrice',
};

/**
 * Pick the estimated cost for the configured type from the caller-supplied
 * stored figures. Null when the figure is missing — the caller renders its
 * explicit empty state (never 0-as-data). Amount normalized ROUND_HALF_UP 2dp.
 */
export function selectEstimatedCost(
  type: CostEstimateType,
  inputs: CostEstimateInputs
): CostEstimateSelection | null {
  const amount = inputs[INPUT_BY_TYPE[type]];
  if (amount === undefined || amount === null) return null;
  return { type, amount: roundHalfUp2(amount) };
}
